import {
  AskStrikeUpButton,
  Avatar,
  Box,
  Button,
  Card,
  FilterChipGroup,
  Flex,
  Heading,
  HStack,
  InfoBox,
  Input,
  RefreshButton,
  Spinner,
  Stack,
  Table,
  Text,
  VStack,
} from "components/ui";
import { useTranslation } from "hooks/useTranslation";
import { useEffect, useState } from "react";
import {
  MdClear,
  MdPerson,
  MdPersonOutline,
  MdSearch,
  MdUploadFile,
  MdVisibility,
} from "react-icons/md";
import { useNavigate } from "react-router-dom";
import {
  CUSTOMER_SEGMENTS,
  Customer,
  CustomerSegment,
  customerService,
} from "services/customerService";
import { getThemeColor } from "theme/colors";

const PAGE_SIZE = 20;

/** Reserved list height so the card doesn't collapse while loading or when empty. */
const LIST_MIN_HEIGHT = "320px";

/**
 * Buckets shown on first load. Widget bootstrap creates a customer row before anyone
 * chats, so an unfiltered list is mostly rows with no name, email or company — this
 * default hides them without hiding anything a user actually did.
 */
const DEFAULT_SEGMENTS: CustomerSegment[] = ["hasSession"];

const Customers = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [filterCompanyId, setFilterCompanyId] = useState("");
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");
  const [segments, setSegments] = useState<CustomerSegment[]>(DEFAULT_SEGMENTS);

  // Server-side paging — reload whenever any filter or page changes.
  useEffect(() => {
    loadCustomers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, filterCompanyId, filterDateFrom, filterDateTo, segments, page]);

  const loadCustomers = async () => {
    try {
      setLoading(true);
      const result = await customerService.getAllCustomers({
        search: search || undefined,
        // filterCompanyId is currently a free-text input that searches by name/legacy.
        // Pass through `search` so server treats it as the same fuzzy match field; the
        // controller also accepts a strict customerCompanyId guid for future use.
        dateFrom: filterDateFrom || undefined,
        dateTo: filterDateTo || undefined,
        segments,
        page,
        pageSize: PAGE_SIZE,
      });
      setCustomers(result.items);
      setTotal(result.total);
      setError("");
    } catch (err: any) {
      setError(err.response?.data?.error || t("app.customers.loadFailed"));
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPage(1);
    setSearch(searchInput);
  };

  // Clearing drops the segment chips too, so one click reveals every row — including
  // the bootstrap ghosts the default view hides.
  const handleClearFilters = () => {
    setSearchInput("");
    setSearch("");
    setFilterCompanyId("");
    setFilterDateFrom("");
    setFilterDateTo("");
    setSegments([]);
    setPage(1);
  };

  // When a non-search filter changes, reset to page 1.
  const handleFilterCompanyChange = (v: string) => {
    setFilterCompanyId(v);
    setPage(1);
  };
  const handleDateFromChange = (v: string) => {
    setFilterDateFrom(v);
    setPage(1);
  };
  const handleDateToChange = (v: string) => {
    setFilterDateTo(v);
    setPage(1);
  };
  const handleSegmentToggle = (segment: CustomerSegment, next: boolean) => {
    setSegments((prev) =>
      next ? [...prev, segment] : prev.filter((s) => s !== segment),
    );
    setPage(1);
  };

  const hasActiveFilters =
    search ||
    filterCompanyId ||
    filterDateFrom ||
    filterDateTo ||
    segments.length > 0;

  // Server returns already-filtered + paged results. Keep a local alias to minimise
  // churn elsewhere in the component.
  const filteredCustomers = customers;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // Only the very first load has nothing to show. Every later load keeps the
  // previous rows on screen (dimmed) so the page never collapses and re-expands
  // while a filter is being changed.
  const isInitialLoad = loading && customers.length === 0;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <Stack gap={4} w="full" maxW="full">
      <Flex justify="space-between" align="center" wrap="wrap" gap={3}>
        <Heading>{t("app.customers.title")}</Heading>
        <HStack gap={3}>
          <AskStrikeUpButton context={t("app.askStrikeUp.context.customers")} />
          <Button
            intent="secondary"
            onClick={() => navigate("/admin/customers-import")}
          >
            <MdUploadFile />{" "}
            {t("app.customers.importButton", {
              defaultValue: "Import from CSV",
            })}
          </Button>
          <RefreshButton onClick={loadCustomers} loading={loading} />
        </HStack>
      </Flex>

      {error && (
        <InfoBox variant="error">
          <Text color="red.700">{error}</Text>
        </InfoBox>
      )}

      {/* Search and Filters */}
      <Card.Root
        borderWidth="1px"
        borderColor={getThemeColor("card.border")}
        borderRadius="md"
      >
        <Card.Body>
          <Stack gap={3}>
            <HStack gap={3}>
              <Input
                placeholder={t("app.customers.searchPlaceholder")}
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                flex={1}
              />
              <Button onClick={handleSearch} intent="primary">
                <MdSearch /> {t("app.common.search")}
              </Button>
              {hasActiveFilters && (
                <Button intent="secondary" onClick={handleClearFilters}>
                  <MdClear /> {t("app.common.clearFilters")}
                </Button>
              )}
            </HStack>

            <HStack gap={3} flexWrap="wrap">
              <Input
                placeholder={t("app.customers.filterByCompanyId")}
                value={filterCompanyId}
                onChange={(e) => handleFilterCompanyChange(e.target.value)}
                w="220px"
              />
              <HStack gap={2}>
                <Text
                  fontSize="sm"
                  color="var(--text-secondary-color)"
                  whiteSpace="nowrap"
                >
                  {t("app.customers.lastUpdatedFrom")}
                </Text>
                <Input
                  type="date"
                  value={filterDateFrom}
                  onChange={(e) => handleDateFromChange(e.target.value)}
                  w="170px"
                />
              </HStack>
              <HStack gap={2}>
                <Text
                  fontSize="sm"
                  color="var(--text-secondary-color)"
                  whiteSpace="nowrap"
                >
                  {t("app.customers.lastUpdatedTo")}
                </Text>
                <Input
                  type="date"
                  value={filterDateTo}
                  onChange={(e) => handleDateToChange(e.target.value)}
                  w="170px"
                />
              </HStack>
            </HStack>

            {/* Activity / origin buckets. Chips OR together on the server; with none
                selected the list is unfiltered. */}
            <HStack gap={3} flexWrap="wrap">
              <Text
                fontSize="sm"
                color="var(--text-secondary-color)"
                whiteSpace="nowrap"
              >
                {t("app.customers.segments.label")}
              </Text>
              <FilterChipGroup
                value={segments}
                onChange={handleSegmentToggle}
                options={CUSTOMER_SEGMENTS.map((segment) => ({
                  id: segment,
                  label: t(`app.customers.segments.${segment}`),
                }))}
              />
            </HStack>
          </Stack>
        </Card.Body>
      </Card.Root>

      <Card.Root
        borderWidth="1px"
        borderColor={getThemeColor("card.border")}
        borderRadius="md"
        overflow="hidden"
      >
        <Card.Body p={0}>
          {isInitialLoad ? (
            // Only the list swaps to a loading state — the header and the whole
            // filter block stay mounted, so toggling a chip never shifts the page.
            <VStack gap={3} py={12} minH={LIST_MIN_HEIGHT} justify="center">
              <Spinner />
              <Text fontSize="sm" color="var(--text-secondary-color)">
                {t("app.customers.loading")}
              </Text>
            </VStack>
          ) : filteredCustomers.length === 0 ? (
            <VStack gap={4} py={8} minH={LIST_MIN_HEIGHT} justify="center">
              <MdPersonOutline size={64} color="gray" />
              <Text fontSize="lg">
                {hasActiveFilters
                  ? t("app.customers.noResults")
                  : t("app.customers.noCustomers")}
              </Text>
              <Text
                fontSize="sm"
                color="var(--text-secondary-color)"
                textAlign="center"
              >
                {hasActiveFilters
                  ? t("app.customers.noResultsDescription")
                  : t("app.customers.noCustomersDescription")}
              </Text>
            </VStack>
          ) : (
            <Box
              overflowX="auto"
              minH={LIST_MIN_HEIGHT}
              // Refetch with rows already on screen: dim in place rather than
              // unmounting the table, so the layout holds still.
              opacity={loading ? 0.5 : 1}
              pointerEvents={loading ? "none" : "auto"}
              transition="opacity 0.15s"
              aria-busy={loading}
            >
              <Table.Root minW="700px">
                <Table.Header>
                  <Table.Row>
                    <Table.ColumnHeader>
                      {t("app.customers.tableHeaders.customer")}
                    </Table.ColumnHeader>
                    <Table.ColumnHeader>
                      {t("app.customers.tableHeaders.email")}
                    </Table.ColumnHeader>
                    <Table.ColumnHeader>
                      {t("app.customers.tableHeaders.companyId")}
                    </Table.ColumnHeader>
                    <Table.ColumnHeader>
                      {t("app.customers.tableHeaders.firstSeen")}
                    </Table.ColumnHeader>
                    <Table.ColumnHeader>
                      {t("app.customers.tableHeaders.lastUpdated")}
                    </Table.ColumnHeader>
                    <Table.ColumnHeader>
                      {t("app.customers.tableHeaders.lastAnalyzed")}
                    </Table.ColumnHeader>
                    <Table.ColumnHeader>
                      {t("app.customers.tableHeaders.actions")}
                    </Table.ColumnHeader>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {filteredCustomers.map((customer) => (
                    <Table.Row
                      key={customer.id}
                      cursor="pointer"
                      onClick={() =>
                        navigate(`/admin/customers/${customer.id}`)
                      }
                    >
                      <Table.Cell>
                        <HStack>
                          <Avatar.Root
                            name={customer.customerName || customer.customerId}
                            size="sm"
                          >
                            <Avatar.Fallback>
                              <MdPerson />
                            </Avatar.Fallback>
                          </Avatar.Root>
                          <Text fontWeight="medium">
                            {customer.customerName || customer.customerId}
                          </Text>
                        </HStack>
                      </Table.Cell>
                      <Table.Cell>
                        <Text
                          fontSize="sm"
                          color={
                            customer.customerEmail
                              ? undefined
                              : "var(--text-secondary-color)"
                          }
                        >
                          {customer.customerEmail ||
                            t("app.common.notAvailable")}
                        </Text>
                      </Table.Cell>
                      <Table.Cell>
                        <Text
                          fontSize="sm"
                          color={
                            customer.customerCompanyName ||
                            customer.customerCompanyId
                              ? undefined
                              : "var(--text-secondary-color)"
                          }
                        >
                          {customer.customerCompanyName ||
                            customer.customerCompanyId ||
                            t("app.common.notAvailable")}
                        </Text>
                      </Table.Cell>
                      <Table.Cell>{formatDate(customer.createdAt)}</Table.Cell>
                      <Table.Cell>
                        {formatDate(customer.lastUpdatedAt)}
                      </Table.Cell>
                      <Table.Cell>
                        <Text
                          fontSize="sm"
                          color={
                            customer.personaLastAnalyzedAt
                              ? undefined
                              : "var(--text-secondary-color)"
                          }
                        >
                          {customer.personaLastAnalyzedAt
                            ? formatDate(customer.personaLastAnalyzedAt)
                            : t("app.customers.notAnalyzed")}
                        </Text>
                      </Table.Cell>
                      <Table.Cell>
                        <Button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/admin/customers/${customer.id}`);
                          }}
                          size="sm"
                          intent="ghost"
                          title={t("app.customers.viewDetails")}
                        >
                          <MdVisibility />
                        </Button>
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table.Root>
            </Box>
          )}
        </Card.Body>
      </Card.Root>

      {total > 0 && (
        <Flex justify="space-between" align="center" wrap="wrap" gap={2} px={2}>
          <Text fontSize="sm" color="var(--text-secondary-color)">
            {t("app.customers.pageStatus", {
              defaultValue: "Showing {{from}}–{{to}} of {{total}}",
              from: (page - 1) * PAGE_SIZE + 1,
              to: Math.min(page * PAGE_SIZE, total),
              total,
            })}
          </Text>
          <HStack gap={2}>
            <Button
              intent="ghost"
              disabled={page === 1 || loading}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              {t("app.common.previous", { defaultValue: "Previous" })}
            </Button>
            <Text fontSize="sm" color="var(--text-secondary-color)">
              {t("app.common.pageOf", {
                defaultValue: "Page {{page}} of {{totalPages}}",
                page,
                totalPages,
              })}
            </Text>
            <Button
              intent="ghost"
              disabled={page >= totalPages || loading}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              {t("app.common.next", { defaultValue: "Next" })}
            </Button>
          </HStack>
        </Flex>
      )}
    </Stack>
  );
};

export default Customers;
